import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useAppSelector } from "../../../store/hooks";
import { LiveSessionQuestion } from "../../../store/slices/liveSessionSlice";

const SendQuestionTab = ({
  updateQuestion,
  setIsSendingQuestion,
}: {
  updateQuestion: (question: LiveSessionQuestion | null) => Promise<void>;
  setIsSendingQuestion: (isSending: boolean) => void;
}) => {
  const [language, setLanguage] = useState("عربي");
  const [answersCount, setAnswersCount] = useState(0);
  const [answers, setAnswers] = useState([]);
  const { question, studentsAnswers, students, studentsNames } = useAppSelector(
    (state) => state.liveSession
  );

  const arabicLetters = "أبجدهوزحطيكلمن".split("");

  const handleAnswersCountChange = (e) => {
    const count = Math.max(0, Math.min(10, parseInt(e.target.value) || 0));
    setAnswersCount(count);
    setAnswers(
      Array.from({ length: count }, (_, i) => ({
        id: i,
        text:
          language === "English"
            ? String.fromCharCode(65 + i)
            : arabicLetters[i],
        isCorrect: false,
      }))
    );
  };

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    setLanguage(selectedLanguage);
    setAnswers((prevAnswers) =>
      prevAnswers.map((answer, i) => ({
        ...answer,
        text:
          selectedLanguage === "English"
            ? String.fromCharCode(65 + i)
            : arabicLetters[i],
      }))
    );
  };

  const handleAnswerChange = (index, field, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index][field] = value;
    setAnswers(updatedAnswers);
  };

  const handleSendQuestion = () => {
    updateQuestion({ language, answers, timeup: false });
  };

  const handleEndQuestion = async () => {
    await updateQuestion(null);
    setIsSendingQuestion(false);
  };

  const handleSendTimeup = () => {
    updateQuestion({ language, answers, timeup: true });
  };

  if (question && question.timeup) {
    const correctAnswers = studentsAnswers.filter((answer) =>
      answer.answers.every((ans) =>
        question.answers.find((qAns) => qAns.id === ans && qAns.isCorrect)
      )
    );
    const wrongAnswers = studentsAnswers.filter(
      (answer) =>
        !answer.answers.every((ans) =>
          question.answers.find((qAns) => qAns.id === ans && qAns.isCorrect)
        )
    );

    const unansweredStudents = students
      .filter(
        (studentId) =>
          !studentsAnswers.some((answer) => answer.studentId === studentId)
      )
      .map((studentId) => ({
        studentId,
        studentName: studentsNames[studentId],
        answers: ["لا يوجد إجابة"],
      }));

    const correctPercentage =
      (correctAnswers.length / studentsAnswers.length) * 100 || 0;
    const wrongPercentage =
      (wrongAnswers.length / studentsAnswers.length) * 100 || 0;

    return (
      <div
        className="flex flex-col"
        style={{
          overflowY: "scroll",
        }}
      >
        <div className="text-green-700 bg-green-100 border border-green-500 rounded-md p-2 text-success font-bold my-1">
          إجابات صحيحة : {correctAnswers.length} (
          {Math.floor(correctPercentage)}%)
        </div>
        <div className="text-red-600 bg-red-100 border border-red-400 rounded-md p-2 text-danger font-bold my-1">
          إجابات خاطئة : {wrongAnswers.length} ({Math.ceil(wrongPercentage)}%)
        </div>
        <div className="border border-primary bg-ea-base rounded-md p-2 text-primary font-bold mt-1 mb-4">
          لا يوجد إجابة : {unansweredStudents.length}
        </div>
        <>
          {studentsAnswers.map((student) => {
            const isCorrect = student.answers.every((ans) =>
              question.answers.find((qAns) => qAns.id === ans && qAns.isCorrect)
            );
            const answerText = student.answers
              .map(
                (ans) =>
                  question.answers.find((qAns) => qAns.id === ans)?.text || ""
              )
              .join(", ");
            return (
              <div
                key={student.studentId}
                className={`border rounded-md p-2 ${
                  isCorrect
                    ? "border border-green-500 bg-green-50 text-green-700"
                    : "border border-red-400 bg-red-50 text-red-600"
                }`}
              >
                <p className="font-bold py-0 mb-1">{student.studentName}</p>
                <p className="py-0 my-1">{answerText}</p>
              </div>
            );
          })}
          {unansweredStudents.map((student) => (
            <div
              key={student.studentId}
              className="rounded-md p-2 border border-primary bg-ea-base text-primary"
            >
              <p className="font-bold py-0 mb-1">
                {student.studentName.split(" ").slice(0, 2).join(" ")}
              </p>
              <p className="py-0 my-1">{student.answers[0]}</p>
            </div>
          ))}
        </>
        <Button
          className="mt-3"
          style={{ maxHeight: "2.5rem" }}
          onClick={handleEndQuestion}
        >
          إنهاء السؤال
        </Button>
      </div>
    );
  }

  if (question && !question.timeup) {
    return (
      <Button
        className="mt-3"
        style={{ maxHeight: "2.5rem" }}
        onClick={handleSendTimeup}
      >
        تسليم الإجابات
      </Button>
    );
  }

  return (
    <>
      <div className="mb-3">
        <label
          htmlFor="languageSelect"
          className="block mb-1 font-medium text-gray-700"
        >
          إختر لغة السؤال
        </label>
        <select
          id="languageSelect"
          className="block w-full px-3 py-1 border border-gray-300 rounded-md bg-white"
          value={language}
          onChange={handleLanguageChange}
        >
          <option value="English">English</option>
          <option value="عربي">عربي</option>
        </select>
      </div>

      <div className="mb-3">
        <label
          htmlFor="answersCount"
          className="block mb-1 font-medium text-gray-700"
        >
          حدد عدد الإجابات
        </label>
        <input
          id="answersCount"
          type="number"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
          min="0"
          max="10"
          value={answersCount}
          onChange={handleAnswersCountChange}
        />
      </div>

      <ul className="list-none p-0 m-0">
        {answers.map((answer, index) => (
          <li key={answer.id} className="flex items-center mb-2">
            <input
              type="checkbox"
              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary me-2 bg-white"
              checked={answer.isCorrect}
              onChange={(e) =>
                handleAnswerChange(index, "isCorrect", e.target.checked)
              }
            />
            <input
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
              value={answer.text}
              onChange={(e) =>
                handleAnswerChange(index, "text", e.target.value)
              }
            />
          </li>
        ))}
      </ul>

      {answers?.some((answer) => answer.isCorrect) && (
        <Button
          className="mt-3"
          style={{ maxHeight: "2.5rem" }}
          onClick={handleSendQuestion}
        >
          إرسل السؤال
        </Button>
      )}
    </>
  );
};

export default SendQuestionTab;
